import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  Alert,
  Loader,
  ActionIcon,
  Tooltip,
  Modal,
  CopyButton,
  Code,
  Grid,
  UnstyledButton,
  Collapse
} from '@mantine/core';
import {
  IconArrowLeft,
  IconPlus,
  IconAlertCircle,
  IconRefresh,
  IconTrash,
  IconEye,
  IconCopy,
  IconCheck,
  IconDownload,
  IconPlayerPlayFilled,
  IconFolder,
  IconFolderOpen,
  IconChevronRight,
  IconChevronDown,
  IconUser
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import YAML from 'yaml';
import { listPlans, getPlan, deletePlan } from '../api_scripts/plansClient';
import { isSitBackendConfigured } from '../api_scripts/sitApiClient';
import { useAuth } from '../../../shared/contexts/AuthContext';
import PreconditionsBadge from './PreconditionsBadge';

const UNCATEGORIZED = 'Uncategorized';

function PlanLibrary({ onBack }) {
  const navigate = useNavigate();
  const backendConfigured = isSitBackendConfigured();
  // AuthContext exposes the evaluated `user` object (not getUserInfo) on
  // the context value — destructure user.email directly.
  const { user } = useAuth();
  const myEmail = (user?.email || '').toLowerCase();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [previewBody, setPreviewBody] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Sidebar selection — null = nothing picked (blank pane, prompt to pick),
  // 'all' = explicit "All plans", `${engineer}` = engineer-only,
  // `${engineer}/${subfolder}` = single folder. Engineer-level expand state
  // tracked separately so the tree remembers which engineer rows are open.
  const [selection, setSelection] = useState(null);
  const [expandedEngineers, setExpandedEngineers] = useState(() => new Set());

  const refresh = useCallback(async () => {
    if (!backendConfigured) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listPlans();
      setRows(list);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [backendConfigured]);

  useEffect(() => { refresh(); }, [refresh]);

  // Build the engineer → subfolder → count tree directly from rows so the
  // sidebar stays in sync with deletions without a second roundtrip.
  const tree = useMemo(() => {
    const byEngineer = new Map();
    for (const row of rows || []) {
      const engineer = (row.createdBy || 'unknown').toLowerCase();
      const subfolder = (row.subfolder || UNCATEGORIZED).trim() || UNCATEGORIZED;
      if (!byEngineer.has(engineer)) byEngineer.set(engineer, new Map());
      const subs = byEngineer.get(engineer);
      subs.set(subfolder, (subs.get(subfolder) ?? 0) + 1);
    }
    return Array.from(byEngineer.entries())
      .map(([engineer, subs]) => ({
        engineer,
        subfolders: Array.from(subs.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => {
            if (a.name === UNCATEGORIZED && b.name !== UNCATEGORIZED) return 1;
            if (b.name === UNCATEGORIZED && a.name !== UNCATEGORIZED) return -1;
            return a.name.localeCompare(b.name);
          }),
        total: Array.from(subs.values()).reduce((s, c) => s + c, 0)
      }))
      // My folders first, then alphabetical.
      .sort((a, b) => {
        if (a.engineer === myEmail) return -1;
        if (b.engineer === myEmail) return 1;
        return a.engineer.localeCompare(b.engineer);
      });
  }, [rows, myEmail]);

  // Auto-expand the current engineer's row when the tree first builds so
  // their folders are visible without an extra click.
  useEffect(() => {
    if (myEmail && tree.some((t) => t.engineer === myEmail)) {
      setExpandedEngineers((prev) => (prev.has(myEmail) ? prev : new Set([...prev, myEmail])));
    }
  }, [myEmail, tree]);

  const visibleRows = useMemo(() => {
    if (!rows) return [];
    if (selection === null) return null; // nothing picked yet — render prompt
    if (selection === 'all') return rows;
    const [eng, sub] = selection.split('/');
    return rows.filter((r) => {
      const rowEng = (r.createdBy || 'unknown').toLowerCase();
      if (rowEng !== eng) return false;
      if (!sub) return true;
      const rowSub = (r.subfolder || UNCATEGORIZED).trim() || UNCATEGORIZED;
      return rowSub === sub;
    });
  }, [rows, selection]);

  const toggleEngineer = (engineer) => {
    setExpandedEngineers((prev) => {
      const next = new Set(prev);
      if (next.has(engineer)) next.delete(engineer);
      else next.add(engineer);
      return next;
    });
  };

  const openPreview = async (id) => {
    setPreviewId(id);
    setPreviewBody(null);
    setPreviewLoading(true);
    try {
      const row = await getPlan(id);
      setPreviewBody(row);
    } catch (err) {
      setPreviewBody({ error: err?.response?.data?.message || err.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewId(null);
    setPreviewBody(null);
  };

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete plan "${id}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await deletePlan(id);
      setRows((prev) => (prev || []).filter((r) => r.id !== id));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const downloadYaml = (plan) => {
    const yaml = YAML.stringify(plan);
    const blob = new Blob([yaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${plan.metadata?.id || 'plan'}.yaml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const sendToRun = (plan) => {
    navigate('/run', { state: { prefillYaml: YAML.stringify(plan) } });
  };

  const previewYaml = previewBody?.plan ? YAML.stringify(previewBody.plan) : '';

  return (
    <Container size="xl" py="lg">
      <Group justify="space-between" mb="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          Back to SIT Home
        </Button>
        <Group gap="xs">
          <Tooltip label="Refresh list">
            <ActionIcon variant="light" onClick={refresh} disabled={!backendConfigured || loading}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Button
            color="violet"
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/plans/new')}
          >
            New Test Plan
          </Button>
        </Group>
      </Group>

      <Stack gap="md">
        <Title order={2}>Test Plan Library</Title>

        {!backendConfigured && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            SIT backend URL is not configured. Set <Code>REACT_APP_SIT_API_BASE</Code> in your
            <Code>.env</Code> to enable the library.
          </Alert>
        )}

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} onClose={() => setError(null)} withCloseButton>
            {error}
          </Alert>
        )}

        {loading && (
          <Group gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Loading plans…</Text>
          </Group>
        )}

        {!loading && rows !== null && rows.length === 0 && (
          <Card withBorder padding="lg" radius="md">
            <Stack gap="xs">
              <Text fw={600}>No plans saved yet</Text>
              <Text size="sm" c="dimmed">
                Import a diagram or Excel test-script, verify the result, and save approved plans
                from the Verify Plans screen — they'll show up here.
              </Text>
            </Stack>
          </Card>
        )}

        {!loading && rows && rows.length > 0 && (
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, sm: 5, md: 4 }}>
              <Card withBorder padding="sm" radius="md">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Folders</Text>
                  <FolderTreeButton
                    icon={<IconFolder size={14} />}
                    label="All plans"
                    count={rows.length}
                    active={selection === 'all'}
                    onClick={() => setSelection('all')}
                  />
                  {tree.map((entry) => {
                    const isMe = entry.engineer === myEmail;
                    const expanded = expandedEngineers.has(entry.engineer);
                    return (
                      <Stack key={entry.engineer} gap={2}>
                        <FolderTreeButton
                          icon={expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                          secondaryIcon={<IconUser size={12} />}
                          label={isMe ? 'You' : entry.engineer}
                          count={entry.total}
                          active={selection === entry.engineer}
                          onClick={() => {
                            setSelection(entry.engineer);
                            toggleEngineer(entry.engineer);
                          }}
                        />
                        <Collapse in={expanded}>
                          <Stack gap={2} ml={20}>
                            {entry.subfolders.map((sub) => {
                              const key = `${entry.engineer}/${sub.name}`;
                              const active = selection === key;
                              return (
                                <FolderTreeButton
                                  key={key}
                                  icon={active ? <IconFolderOpen size={14} /> : <IconFolder size={14} />}
                                  label={sub.name}
                                  count={sub.count}
                                  active={active}
                                  onClick={() => setSelection(key)}
                                />
                              );
                            })}
                          </Stack>
                        </Collapse>
                      </Stack>
                    );
                  })}
                </Stack>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 7, md: 8 }}>
              {visibleRows === null ? (
                <Card withBorder padding="lg" radius="md">
                  <Text size="sm" c="dimmed">Pick a folder on the left to view plans.</Text>
                </Card>
              ) : visibleRows.length === 0 ? (
                <Card withBorder padding="lg" radius="md">
                  <Text size="sm" c="dimmed">No plans in this folder.</Text>
                </Card>
              ) : (
                <Stack gap="sm">
                  {visibleRows.map((row) => (
                    <Card key={row.id} withBorder padding="md" radius="sm">
                      <Group justify="space-between" wrap="nowrap">
                        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                          <Group gap="xs">
                            <Text fw={600}>{row.name}</Text>
                            <Badge size="xs" variant="light" color="gray">{row.id}</Badge>
                            {row.subfolder && (
                              <Badge size="xs" variant="light" color="violet" leftSection={<IconFolder size={10} />}>
                                {row.subfolder}
                              </Badge>
                            )}
                            {(row.tags || []).map((t) => (
                              <Badge key={t} variant="dot" color="violet" size="xs">{t}</Badge>
                            ))}
                            <PreconditionsBadge preconditions={row.preconditions} />
                          </Group>
                          {row.notes && (
                            <Text size="xs" c="dimmed" lineClamp={1}>{row.notes}</Text>
                          )}
                          <Text size="xs" c="dimmed">
                            created {new Date(row.createdAt).toLocaleString()} by {row.createdBy || 'unknown'}
                          </Text>
                        </Stack>
                        <Group gap="xs" wrap="nowrap">
                          <Tooltip label="Preview YAML">
                            <ActionIcon variant="subtle" onClick={() => openPreview(row.id)}>
                              <IconEye size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Delete plan">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => handleDelete(row.id)}
                              loading={deletingId === row.id}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              )}
            </Grid.Col>
          </Grid>
        )}
      </Stack>

      <Modal
        opened={previewId !== null}
        onClose={closePreview}
        title={previewId ? `Plan: ${previewId}` : 'Plan'}
        size="lg"
      >
        {previewLoading && (
          <Group gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Loading plan body…</Text>
          </Group>
        )}
        {previewBody?.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>{previewBody.error}</Alert>
        )}
        {previewBody?.plan && (
          <Stack gap="sm">
            <Group justify="flex-end" gap="xs">
              <CopyButton value={previewYaml}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy YAML'}>
                    <ActionIcon variant="subtle" onClick={copy}>
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
              <Tooltip label="Download YAML">
                <ActionIcon variant="subtle" onClick={() => downloadYaml(previewBody.plan)}>
                  <IconDownload size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Open in Run Trigger">
                <ActionIcon variant="subtle" onClick={() => sendToRun(previewBody.plan)}>
                  <IconPlayerPlayFilled size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <Code block style={{ maxHeight: 380, overflow: 'auto', fontSize: 11 }}>
              {previewYaml}
            </Code>
          </Stack>
        )}
      </Modal>
    </Container>
  );
}

/**
 * One row in the library sidebar tree — handles selected styling, count
 * badge, optional second icon (user icon next to engineer rows). Kept tiny
 * on purpose; the tree itself lives inline in PlanLibrary.
 */
function FolderTreeButton({ icon, secondaryIcon, label, count, active, onClick }) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        padding: '4px 8px',
        borderRadius: 4,
        backgroundColor: active ? 'var(--mantine-color-violet-light)' : 'transparent',
        cursor: 'pointer'
      }}
    >
      <Group gap={6} wrap="nowrap">
        {icon}
        {secondaryIcon}
        <Text size="sm" fw={active ? 600 : 400} style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
          {label}
        </Text>
        {typeof count === 'number' && (
          <Badge size="xs" variant="light" color={active ? 'violet' : 'gray'}>
            {count}
          </Badge>
        )}
      </Group>
    </UnstyledButton>
  );
}

export default PlanLibrary;
